/**
 * Student fee invoices — the settings form's twin of vc_be/utils/feeInvoice.js.
 *
 * Every invoice on screen is the server's PDF (the fee page and the settings
 * preview both draw it), so nothing here decides what an invoice says. This
 * copy is what the forms check as you type: a GSTIN, a place of supply, a GST
 * rate, and the "₹10,000 becomes…" examples. Both test files pin the same GST
 * and GSTIN cases — change both.
 */

export const DEFAULT_SAC = "999293";
export const DEFAULT_DESIGNATION = "Authorised Signatory";
export const MAX_RATE = 40;
export const COMMON_RATES = [5, 12, 18, 28, 40];

export const STATES = Object.freeze({
  "01": "Jammu and Kashmir", "02": "Himachal Pradesh", "03": "Punjab", "04": "Chandigarh",
  "05": "Uttarakhand", "06": "Haryana", "07": "Delhi", "08": "Rajasthan", "09": "Uttar Pradesh",
  "10": "Bihar", "11": "Sikkim", "12": "Arunachal Pradesh", "13": "Nagaland", "14": "Manipur",
  "15": "Mizoram", "16": "Tripura", "17": "Meghalaya", "18": "Assam", "19": "West Bengal",
  "20": "Jharkhand", "21": "Odisha", "22": "Chhattisgarh", "23": "Madhya Pradesh", "24": "Gujarat",
  "26": "Dadra and Nagar Haveli and Daman and Diu", "27": "Maharashtra", "29": "Karnataka",
  "30": "Goa", "31": "Lakshadweep", "32": "Kerala", "33": "Tamil Nadu", "34": "Puducherry",
  "35": "Andaman and Nicobar Islands", "36": "Telangana", "37": "Andhra Pradesh", "38": "Ladakh",
  "97": "Other Territory",
});
const LEGACY_STATES = Object.freeze({ "25": "Daman and Diu", "28": "Andhra Pradesh" });

export const STATE_OPTIONS = Object.entries(STATES)
  .map(([code, name]) => ({ value: code, label: `${name} (${code})` }))
  .sort((a, b) => a.label.localeCompare(b.label));

export function stateOf(code) {
  const c = String(code ?? "").trim();
  const name = STATES[c] || LEGACY_STATES[c];
  return name ? { code: c, name } : null;
}

/** "Karnataka (29)" */
export const stateLabel = (state) => (state ? `${state.name} (${state.code})` : "");

export function normaliseGstin(value) {
  return String(value ?? "").toUpperCase().replace(/[\s-]/g, "");
}

/** What makes a GSTIN unusable, as a sentence — or null. The server refuses these. */
export function gstinProblem(value) {
  const g = normaliseGstin(value);
  if (!g) return null;
  if (!/^[0-9A-Z]{15}$/.test(g)) return "A GSTIN is 15 letters and digits.";
  if (!stateOf(g.slice(0, 2))) return `A GSTIN starts with a state code, and "${g.slice(0, 2)}" is not one.`;
  return null;
}

const GSTIN_CHARS = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ";
const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

function gstinCheckChar(first14) {
  let factor = 2;
  let sum = 0;
  for (let i = first14.length - 1; i >= 0; i -= 1) {
    const addend = factor * GSTIN_CHARS.indexOf(first14[i]);
    factor = factor === 2 ? 1 : 2;
    sum += Math.floor(addend / 36) + (addend % 36);
  }
  return GSTIN_CHARS[(36 - (sum % 36)) % 36];
}

/** Standard pattern + check character. A warning when false, never a refusal. */
export function gstinLooksRight(value) {
  const g = normaliseGstin(value);
  return GSTIN_PATTERN.test(g) && gstinCheckChar(g.slice(0, 14)) === g[14];
}

/** A GST rate above 0 and up to 40, to two decimals — or null. */
export function validRate(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0 || n > MAX_RATE) return null;
  return Math.round(n * 100) / 100;
}

const toPaise = (rupees) => Math.round((Number(rupees) || 0) * 100);
const toRupees = (paise) => Math.round(paise) / 100;

/** Taxable value, GST and totals for one payment, in rupees. */
export function gstBreakdown({ amount, refund = 0, rate, pricing = "inclusive", interState = false }) {
  const r = validRate(rate) ?? 0;
  const amountP = Math.max(0, toPaise(amount));
  const refundP = Math.min(Math.max(0, toPaise(refund)), amountP);

  let taxableP;
  let taxP;
  if (pricing === "exclusive") {
    taxableP = amountP;
    taxP = Math.round((amountP * r) / 100);
  } else {
    taxableP = Math.round((amountP * 100) / (100 + r));
    taxP = amountP - taxableP;
  }
  const totalP = taxableP + taxP;
  const refundGrossP = pricing === "exclusive" ? refundP + Math.round((refundP * r) / 100) : refundP;

  let cgstP = 0;
  let sgstP = 0;
  let igstP = 0;
  if (interState) igstP = taxP;
  else {
    cgstP = Math.round(taxP / 2);
    sgstP = taxP - cgstP;
  }

  return {
    rate: r,
    split: interState ? "inter" : "intra",
    taxable: toRupees(taxableP),
    cgst: toRupees(cgstP),
    sgst: toRupees(sgstP),
    igst: toRupees(igstP),
    tax: toRupees(taxP),
    total: toRupees(totalP),
    refund: toRupees(refundGrossP),
    netPaid: toRupees(Math.max(0, totalP - refundGrossP)),
  };
}
